import { useState, useEffect } from 'react';
import { useView } from '../contexts/ViewContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Box,
  Search,
  Download,
  Upload,
  Pencil,
  Trash2,
  Loader2,
  RefreshCw,
  Save,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '../contexts/ConfirmDialogContext';
import { customersAPI, type CustomerStockLevel } from '../lib/api';
import { useIsCustomer } from '../hooks/usePortalMode';
import { useFormatters } from '../contexts/PreferencesContext';
import { Pagination } from '../components/ui/pagination';
import StockUploadDialog from '../components/customers/StockUploadDialog';

export default function StockLevelsPage() {
  const isCustomer = useIsCustomer();
  const { getViewQueryParams, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewContextLoading } = useView();
  const confirm = useConfirm();
  const [stockLevels, setStockLevels] = useState<CustomerStockLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<{ quantity: string; notes: string }>({ quantity: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [exporting, setExporting] = useState(false);

  const pageSize = 20;

  useEffect(() => {
    if (viewContextLoading) return;
    fetchStockLevels();
  }, [viewContextLoading, currentPage, searchTerm, isCustomer, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId]);

  const fetchStockLevels = async () => {
    try {
      setLoading(true);
      const viewParams = getViewQueryParams();
      const response = await customersAPI.getStockLevels({
        search: searchTerm || undefined,
        page: currentPage,
        page_size: pageSize,
        ordering: 'part_number',
        ...viewParams,
      });
      setStockLevels(response.data.results);
      setTotalCount(response.data.count);
      setTotalPages(Math.ceil(response.data.count / pageSize));
    } catch (err) {
      console.error('Error fetching stock levels:', err);
      toast.error('Failed to load stock levels');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleEdit = (stock: CustomerStockLevel) => {
    setEditingId(stock.id);
    setEditValues({
      quantity: stock.quantity_available.toString(),
      notes: stock.notes || '',
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValues({ quantity: '', notes: '' });
  };

  const handleSaveEdit = async (id: number) => {
    try {
      setSaving(true);
      await customersAPI.updateStockLevel(id, {
        quantity_available: parseFloat(editValues.quantity),
        notes: editValues.notes,
      });
      toast.success('Stock level updated');
      setEditingId(null);
      fetchStockLevels();
    } catch (err) {
      console.error('Error updating stock level:', err);
      toast.error('Failed to update stock level');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({ title: 'Delete Stock Level', description: 'Are you sure you want to delete this stock level?', confirmLabel: 'Delete', variant: 'destructive' });
    if (!ok) return;

    try {
      await customersAPI.deleteStockLevel(id);
      toast.success('Stock level deleted');
      fetchStockLevels();
    } catch (err) {
      console.error('Error deleting stock level:', err);
      toast.error('Failed to delete stock level');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await customersAPI.downloadStockTemplate();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'stock_levels_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading template:', err);
      toast.error('Failed to download template');
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await customersAPI.exportStockLevels({
        part_number: searchTerm || undefined,
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `stock_levels_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Stock levels exported');
    } catch (err) {
      console.error('Error exporting:', err);
      toast.error('Failed to export stock levels');
    } finally {
      setExporting(false);
    }
  };

  const { formatDateTime: contextFormatDateTime } = useFormatters();

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return contextFormatDateTime(dateStr);
  };

  if (loading && stockLevels.length === 0) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Box className="h-5 w-5 text-primary" />
              <CardTitle>Stock Levels</CardTitle>
              <Badge variant="outline">{totalCount} parts</Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchStockLevels}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              {isCustomer && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadTemplate}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Template
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowUploadDialog(true)}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload CSV
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search Bar */}
          <div className="flex items-center gap-4 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by part number..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Part Number</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Qty Available</TableHead>
                  <TableHead>UOM</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead>Updated By</TableHead>
                  <TableHead>Notes</TableHead>
                  {isCustomer && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {stockLevels.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isCustomer ? 8 : 7} className="text-center py-8 text-muted-foreground">
                      No stock levels found. {isCustomer && 'Upload a CSV to add stock data.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  stockLevels.map((stock) => (
                    <TableRow key={stock.id}>
                      <TableCell className="font-medium">{stock.part_number}</TableCell>
                      <TableCell>{stock.supplier_name}</TableCell>
                      <TableCell className="text-right">
                        {editingId === stock.id ? (
                          <Input
                            type="number"
                            value={editValues.quantity}
                            onChange={(e) => setEditValues({ ...editValues, quantity: e.target.value })}
                            className="w-24 text-right"
                            min="0"
                            step="0.001"
                          />
                        ) : (
                          <span className="font-mono">{stock.quantity_available.toLocaleString()}</span>
                        )}
                      </TableCell>
                      <TableCell>{stock.unit_of_measure}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(stock.last_updated)}
                      </TableCell>
                      <TableCell className="text-sm">{stock.updated_by_name || '-'}</TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {editingId === stock.id ? (
                          <Input
                            value={editValues.notes}
                            onChange={(e) => setEditValues({ ...editValues, notes: e.target.value })}
                            placeholder="Notes..."
                          />
                        ) : (
                          <span className="text-sm text-muted-foreground">{stock.notes || '-'}</span>
                        )}
                      </TableCell>
                      {isCustomer && (
                        <TableCell className="text-right">
                          {editingId === stock.id ? (
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSaveEdit(stock.id)}
                                disabled={saving}
                              >
                                {saving ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Save className="h-4 w-4 text-green-600" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleCancelEdit}
                              >
                                <X className="h-4 w-4 text-red-600" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(stock)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(stock.id)}
                              >
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                itemsPerPage={pageSize}
                onPageChange={setCurrentPage}
                itemLabel="stock levels"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <StockUploadDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
        onUploadComplete={() => {
          setShowUploadDialog(false);
          fetchStockLevels();
        }}
      />
    </div>
  );
}
